import {
  type BibleLocation,
  getNextChapter,
  getPreviousChapter,
  getChapterId,
  parseChapterId,
} from '@skjv/common';
import { setupClickHandler, setupDragHandler } from './click-handlers';
import { createChapterObserver, updateCurrentChapterFromObserver } from './chapter-observer';
import { loadChapter, unloadDistantChapters } from './chapter-loader';
import { addBookmarkIndicators, refreshBookmarkIndicators } from './bookmark-indicators';
import { addAnalysisIndicators } from './verse-analysis-indicators';
import {
  applyStudyPassageToChapter,
  clearStudyPassageOutline,
  scrollToStudyPassage,
} from './study-passage-outline';
import { scrollToVerseElement } from './verse-highlight';
import { type ScrollerConfig, DEFAULT_CONFIG } from './scroller-config';

export type { ScrollerConfig };

/**
 * Manages seamless infinite scrolling across Bible chapters.
 * Loads chapters dynamically as user scrolls and unloads distant ones.
 */
export class SeamlessChapterScroller {
  private container: HTMLElement;
  private config: ScrollerConfig;
  loadedChapters: Map<string, HTMLElement> = new Map();
  currentChapterId: string = '';
  private loadingChapters: Set<string> = new Set();
  private isTicking = false;
  isInitializing = false;
  scrollAdjustmentUntil = 0;
  private abortController: AbortController | null = null;
  private initVersion = 0;
  private chapterObserver: IntersectionObserver | null = null;
  visibleChapters: Map<string, number> = new Map();
  private pendingChapterUpdate: number | null = null;
  private pendingUnload: ReturnType<typeof setTimeout> | null = null;
  private lastScrollTime = 0;
  private lastScrollTop = 0;
  private isTouching = false;
  private pendingPrevLoad: ReturnType<typeof setTimeout> | null = null;
  private studyPassageStart: BibleLocation | null = null;
  private studyPassageEnd: BibleLocation | null = null;

  /** Callback when visible chapter changes */
  public onChapterChange?: (location: BibleLocation) => void;

  /** Callback when a Strong's word is clicked */
  public onStrongsClick?: (
    strongsNumbers: string,
    location: BibleLocation,
    element: HTMLElement,
    englishWord?: string,
  ) => void;

  /** Callback when a verse is clicked */
  public onVerseClick?: (location: BibleLocation) => void;

  /** Callback when a pericope heading is clicked */
  public onPericopeClick?: (location: BibleLocation, title: string, anchorRect: DOMRect) => void;

  /** Callback to get bookmarks at a location */
  public getBookmarksAt?: (
    location: BibleLocation,
  ) => Array<{ name: string; isFavorite?: boolean }>;

  constructor(container: HTMLElement, config: Partial<ScrollerConfig> = {}) {
    this.container = container;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.setupScrollListener();
    this.setupTouchTracking();
    setupClickHandler(container, {
      onStrongsClick: (...args) => this.onStrongsClick?.(...args),
      onVerseClick: (...args) => this.onVerseClick?.(...args),
      onPericopeClick: (...args) => this.onPericopeClick?.(...args),
    });
    setupDragHandler(container);
    this.setupChapterObserver();
  }

  private setupScrollListener() {
    this.container.addEventListener('scroll', () => {
      if (!this.isTicking) {
        requestAnimationFrame(() => {
          this.handleScroll();
          this.isTicking = false;
        });
        this.isTicking = true;
      }
    });
  }

  /**
   * iOS Safari ignores programmatic scrollTo while a finger is down or
   * momentum is running, and has no scroll anchoring. Prepending a chapter
   * mid-gesture therefore strands the viewport at the top of the new chapter.
   */
  private setupTouchTracking() {
    const end = () => {
      this.isTouching = false;
      this.schedulePreviousLoad();
    };
    this.container.addEventListener('touchstart', () => { this.isTouching = true; }, { passive: true });
    this.container.addEventListener('touchend', end, { passive: true });
    this.container.addEventListener('touchcancel', end, { passive: true });
  }

  /** Load the previous chapter only once scrolling has fully settled. */
  private schedulePreviousLoad() {
    if (this.pendingPrevLoad !== null) clearTimeout(this.pendingPrevLoad);
    this.pendingPrevLoad = setTimeout(() => {
      this.pendingPrevLoad = null;
      if (this.isTouching || this.isInitializing) return;
      if (Date.now() - this.lastScrollTime < 150) {
        this.schedulePreviousLoad();
        return;
      }
      if (this.container.scrollTop < this.config.loadThreshold) {
        this.loadPreviousChapter();
      }
    }, 200);
  }

  private setupChapterObserver() {
    this.chapterObserver = createChapterObserver(
      this.container,
      this,
      () => this.scheduleChapterUpdate(),
    );
  }

  private scheduleChapterUpdate() {
    if (this.pendingChapterUpdate !== null) {
      cancelAnimationFrame(this.pendingChapterUpdate);
    }
    this.pendingChapterUpdate = requestAnimationFrame(() => {
      this.pendingChapterUpdate = null;
      updateCurrentChapterFromObserver(this.container, this);
    });
  }

  private handleScroll() {
    if (this.isInitializing) return;
    if (Date.now() < this.scrollAdjustmentUntil) return;

    this.lastScrollTime = Date.now();

    const { scrollTop, scrollHeight, clientHeight } = this.container;
    const scrollingUp = scrollTop < this.lastScrollTop;
    this.lastScrollTop = scrollTop;

    // The observer drops entries during scroll adjustments, so the chapter it
    // last reported can go stale — re-derive it from the DOM on every scroll
    const onScreen = this.chapterIdAtViewport();
    if (onScreen) this.currentChapterId = onScreen;

    if (scrollTop + clientHeight > scrollHeight - this.config.loadThreshold) {
      this.loadNextChapter();
    }

    // Only when the reader is heading upward, and only after the gesture settles
    if (scrollingUp && scrollTop < this.config.loadThreshold) {
      this.schedulePreviousLoad();
    }

    this.scheduleUnload();
  }

  private scheduleUnload() {
    if (this.pendingUnload !== null) {
      clearTimeout(this.pendingUnload);
    }
    this.pendingUnload = setTimeout(() => {
      this.pendingUnload = null;
      // iOS drops scrollTo while a finger is down; the next scroll reschedules
      if (Date.now() - this.lastScrollTime >= 250 && !this.isTouching) {
        unloadDistantChapters(
          this.container,
          this.loadedChapters,
          this.visibleChapters,
          this.chapterIdAtViewport() ?? this.currentChapterId,
          this.config.keepChapters,
          (el) => this.chapterObserver?.unobserve(el),
        );
      }
    }, 300);
  }

  /** Id of the loaded chapter spanning the vertical middle of the viewport */
  private chapterIdAtViewport(): string | null {
    const box = this.container.getBoundingClientRect();
    const middle = box.top + box.height / 2;
    for (const [id, el] of this.loadedChapters) {
      const r = el.getBoundingClientRect();
      if (r.top <= middle && r.bottom >= middle) return id;
    }
    return null;
  }

  /** First or last chapter actually in the DOM — the edges we extend from */
  private edgeChapter(edge: 'first' | 'last'): BibleLocation | null {
    const chapters = this.container.querySelectorAll<HTMLElement>('.chapter');
    const el = edge === 'first' ? chapters[0] : chapters[chapters.length - 1];
    return el ? parseChapterId(el.id.replace('chapter-', '')) : null;
  }

  private async loadNextChapter() {
    if (this.isInitializing) return;
    const lastLocation = this.edgeChapter('last');
    if (!lastLocation) return;
    const nextLocation = getNextChapter(lastLocation);
    if (!nextLocation) return;
    const nextId = getChapterId(nextLocation);
    if (this.loadedChapters.has(nextId) || this.loadingChapters.has(nextId)) return;
    await this.doLoadChapter(nextLocation, 'after');
  }

  private async loadPreviousChapter() {
    if (this.isInitializing) return;
    const firstLocation = this.edgeChapter('first');
    if (!firstLocation) return;
    const prevLocation = getPreviousChapter(firstLocation);
    if (!prevLocation) return;
    const prevId = getChapterId(prevLocation);
    if (this.loadedChapters.has(prevId) || this.loadingChapters.has(prevId)) return;

    const heightBefore = this.container.scrollHeight;
    this.scrollAdjustmentUntil = Date.now() + 500;

    if (!(await this.doLoadChapter(prevLocation, 'before'))) return;

    // Read scrollTop after the insert (not before the fetch) so any scrolling
    // during the fetch is kept; overflow-anchor is off, so this is the only shift
    const heightAdded = this.container.scrollHeight - heightBefore;
    if (heightAdded > 0) {
      this.container.scrollTo({
        top: this.container.scrollTop + heightAdded,
        behavior: 'instant',
      });
    }
  }

  private async doLoadChapter(
    location: BibleLocation,
    position: 'before' | 'after',
    signal?: AbortSignal,
  ): Promise<boolean> {
    return loadChapter(
      {
        container: this.container,
        htmlBasePath: this.config.htmlBasePath,
        loadedChapters: this.loadedChapters,
        loadingChapters: this.loadingChapters,
        initVersion: this.initVersion,
        onChapterLoaded: (_id, el) => this.chapterObserver?.observe(el),
        addBookmarkIndicators: (el, loc) => {
          addBookmarkIndicators(el, loc, this.getBookmarksAt);
          addAnalysisIndicators(el, loc);
        },
        applyStudyPassageToChapter: (el) =>
          applyStudyPassageToChapter(el, this.studyPassageStart, this.studyPassageEnd),
      },
      location,
      position,
      signal,
    );
  }

  /**
   * Initialize with a starting location.
   * Uses a fade transition for smooth chapter changes.
   */
  async initialize(location: BibleLocation) {
    const targetChapterId = getChapterId(location);
    console.log('[Scroller] initialize called:', {
      location,
      targetChapterId,
      currentChapterId: this.currentChapterId,
      initVersion: this.initVersion,
      isInitializing: this.isInitializing,
    });

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    this.initVersion++;
    const capturedVersion = this.initVersion;
    console.log('[Scroller] New initVersion:', capturedVersion);

    this.isInitializing = true;

    try {
      if (this.pendingUnload !== null) {
        clearTimeout(this.pendingUnload);
        this.pendingUnload = null;
      }

      this.container.classList.add('chapter-transitioning');
      await new Promise((resolve) => setTimeout(resolve, 150));

      if (this.initVersion !== capturedVersion) {
        console.log('[Scroller] ABORTED after fade - version mismatch:', {
          capturedVersion,
          currentVersion: this.initVersion,
        });
        return;
      }

      console.log('[Scroller] Clearing container, had children:', this.container.childElementCount);
      this.container.innerHTML = '';
      this.loadedChapters.clear();
      this.loadingChapters.clear();
      this.visibleChapters.clear();
      this.currentChapterId = '';

      console.log('[Scroller] Loading main chapter:', targetChapterId);
      const success = await this.doLoadChapter(location, 'after', signal);

      if (this.initVersion !== capturedVersion) {
        console.log('[Scroller] ABORTED after load - version mismatch:', {
          capturedVersion,
          currentVersion: this.initVersion,
        });
        return;
      }

      if (!success) {
        console.error('[Scroller] FAILED to load chapter:', targetChapterId);
        this.container.classList.remove('chapter-transitioning');
        return;
      }

      this.currentChapterId = targetChapterId;

      const firstChapter = this.container.querySelector('.chapter');
      const loadedChapterId = firstChapter?.id?.replace('chapter-', '') || 'none';
      console.log('[Scroller] SUCCESS - loaded chapter:', {
        requested: targetChapterId,
        loaded: loadedChapterId,
        match: targetChapterId === loadedChapterId,
        containerChildren: this.container.childElementCount,
      });

      this.container.scrollTop = 0;
      this.lastScrollTop = 0;

      const scrollBlockDuration = location.verseIndex > 0 ? 3000 : 500;
      this.scrollAdjustmentUntil = Date.now() + scrollBlockDuration;

      this.container.classList.remove('chapter-transitioning');

      const next = getNextChapter(location);
      if (next) {
        setTimeout(async () => {
          if (this.initVersion !== capturedVersion) return;
          console.log('[Scroller] Background loading next chapter:', getChapterId(next));
          await this.doLoadChapter(next, 'after', signal);
        }, 200);
      }

      if (location.verseIndex > 0) {
        setTimeout(() => {
          if (this.initVersion === capturedVersion) {
            this.scrollToVerse(location, true);
          }
        }, 300);
      }

      // Preload the previous chapter once any verse scroll has settled. The
      // reader starts at the top, so an upward scroll can never trigger it.
      setTimeout(() => {
        if (this.initVersion === capturedVersion) this.schedulePreviousLoad();
      }, scrollBlockDuration);
    } finally {
      if (this.initVersion === capturedVersion) {
        this.isInitializing = false;
        console.log('[Scroller] Initialize complete for:', targetChapterId);
      }
    }
  }

  scrollToVerse(location: BibleLocation, highlight = true) {
    scrollToVerseElement(
      this.container,
      location,
      highlight,
      (until) => { this.scrollAdjustmentUntil = until; },
    );
  }

  getCurrentLocation(): BibleLocation | null {
    return parseChapterId(this.currentChapterId);
  }

  isChapterLoaded(location: BibleLocation): boolean {
    return this.loadedChapters.has(getChapterId(location));
  }

  refreshBookmarkIndicators() {
    refreshBookmarkIndicators(this.container, this.loadedChapters, this.getBookmarksAt);
  }

  setStudyPassageOutline(
    startLocation: BibleLocation | null,
    endLocation?: BibleLocation | null,
  ) {
    clearStudyPassageOutline(this.container);

    if (!startLocation) {
      this.studyPassageStart = null;
      this.studyPassageEnd = null;
      return;
    }

    this.studyPassageStart = startLocation;
    this.studyPassageEnd = endLocation || null;

    for (const [, chapterElement] of this.loadedChapters) {
      applyStudyPassageToChapter(chapterElement, this.studyPassageStart, this.studyPassageEnd);
    }
  }

  scrollToStudyPassage(location: BibleLocation) {
    scrollToStudyPassage(
      this.container,
      location,
      (until) => { this.scrollAdjustmentUntil = until; },
    );
  }

  destroy() {
    if (this.abortController) {
      this.abortController.abort();
    }
    if (this.chapterObserver) {
      this.chapterObserver.disconnect();
    }
    if (this.pendingChapterUpdate !== null) {
      cancelAnimationFrame(this.pendingChapterUpdate);
    }
    if (this.pendingUnload !== null) {
      clearTimeout(this.pendingUnload);
    }
    if (this.pendingPrevLoad !== null) {
      clearTimeout(this.pendingPrevLoad);
    }
    this.loadedChapters.clear();
    this.loadingChapters.clear();
    this.visibleChapters.clear();
    this.initVersion++;
  }
}
